import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthSecurity } from "@/hooks/useAuthSecurity";
import type { User } from "@supabase/supabase-js";

interface Purchase {
  product_id: string;
  sku_key: string | null;
  status: string;
}

interface SubscriptionInfo {
  subscribed: boolean;
  tier: string;
  subscription_end: string | null;
  billing_interval: string | null;
  trial_end: string | null;
  cancel_at_period_end: boolean;
  purchases: Purchase[];
}

type Profile = { tier: string | null; ai_credits_remaining: number | null; ai_credits_monthly_allowance: number | null; ai_credits_refreshed_at: string | null; email: string | null; created_at: string };

interface AdminContextType {
  isAdmin: boolean;
  adminMode: boolean;
  setAdminMode: (v: boolean) => void;
  userTier: string;
  user: User | null;
  profile: Profile | null;
  subscription: SubscriptionInfo | null;
  purchases: Purchase[];
  hasPurchased: (skuKey: string) => boolean;
  refreshSubscription: () => Promise<void>;
}

const AdminContext = createContext<AdminContextType>({
  isAdmin: false,
  adminMode: false,
  setAdminMode: () => {},
  userTier: "basic",
  user: null,
  profile: null,
  subscription: null,
  purchases: [],
  hasPurchased: () => false,
  refreshSubscription: async () => {},
});

export const useAdminContext = () => useContext(AdminContext);

/** Value tagged with the account session generation it belongs to. */
type Scoped<T> = { gen: number; value: T };

/**
 * Account isolation: every account-scoped value is tagged with a session
 * generation that increments on every identity change (including A→B→A and
 * logout). Values are only exposed when their generation matches the current
 * one, so a new session never sees the previous session's admin role, admin
 * mode, profile, subscription, purchases or tier — even on the first render
 * before effects run. Async callbacks and retained setters/refresh functions
 * are bound to their generation and cannot write into a later session.
 */
export function AdminProvider({ children }: { children: ReactNode }) {
  const { user } = useAuthSecurity();
  const userId = user?.id ?? null;

  // Session generation, advanced synchronously during render on identity change.
  const lastUserIdRef = useRef<string | null>(userId);
  const genRef = useRef(0);
  if (lastUserIdRef.current !== userId) {
    lastUserIdRef.current = userId;
    genRef.current += 1;
  }
  const gen = genRef.current;

  const [role, setRole] = useState<Scoped<boolean> | null>(null);
  // SECURITY: adminMode is never seeded from browser storage; it requires the
  // CURRENT session's verified admin role.
  const [adminModeState, setAdminModeState] = useState<Scoped<boolean> | null>(null);
  const [profileState, setProfileState] = useState<Scoped<Profile | null> | null>(null);
  const [subState, setSubState] = useState<Scoped<SubscriptionInfo> | null>(null);
  const subRequestRef = useRef(0);
  // Mount lifecycle: false after unmount. StrictMode cleanup/replay flips it
  // false→true without touching the session generation.
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const cur = <T,>(s: Scoped<T> | null): T | undefined => (s && s.gen === gen ? s.value : undefined);
  const isAdmin = !!userId && cur(role) === true;
  const adminMode = isAdmin && cur(adminModeState) === true;
  const profile = userId ? cur(profileState) ?? null : null;
  const subscription = userId ? cur(subState) ?? null : null;
  const purchases = subscription?.purchases || [];

  // Latest verified-admin status for this generation, read by the setter.
  const isAdminRef = useRef(isAdmin);
  isAdminRef.current = isAdmin;

  const setAdminMode = useCallback((v: boolean) => {
    if (!mountedRef.current || genRef.current !== gen) {
      console.warn("[ADMIN_ISOLATION] Ignored stale setAdminMode from a previous session");
      return;
    }
    setAdminModeState({ gen, value: v && isAdminRef.current });
  }, [gen]);

  useEffect(() => {
    // Clear any legacy persisted flag from earlier builds.
    try { sessionStorage.removeItem("seatech_admin_mode"); } catch { /* ignore storage errors */ }
  }, []);

  // Load admin role + profile for this session.
  useEffect(() => {
    if (!userId) return;
    let active = true;
    const isCurrent = () => active && mountedRef.current && genRef.current === gen;

    supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .eq("role", "admin")
      .maybeSingle()
      .then(({ data }) => {
        if (!isCurrent()) return;
        const admin = !!data;
        setRole({ gen, value: admin });
        if (!admin) setAdminModeState({ gen, value: false });
      }, (err: unknown) => {
        if (!isCurrent()) return;
        // Fail closed: an unverifiable role is not admin.
        console.error("[ADMIN_ISOLATION] admin role lookup failed; treating as not admin", err);
        setRole({ gen, value: false });
        setAdminModeState({ gen, value: false });
      });

    supabase
      .from("profiles")
      .select("tier, ai_credits_remaining, ai_credits_monthly_allowance, ai_credits_refreshed_at, email, created_at")
      .eq("id", userId)
      .maybeSingle()
      .then(({ data }) => {
        if (!isCurrent()) return;
        setProfileState({ gen, value: data ? {
          tier: data.tier,
          ai_credits_remaining: data.ai_credits_remaining,
          ai_credits_monthly_allowance: data.ai_credits_monthly_allowance,
          ai_credits_refreshed_at: data.ai_credits_refreshed_at,
          email: data.email,
          created_at: data.created_at,
        } : null });
      }, (err: unknown) => {
        if (!isCurrent()) return;
        console.error("[ADMIN_ISOLATION] profile lookup failed", err);
        setProfileState({ gen, value: null });
      });

    return () => { active = false; };
  }, [userId, gen]);

  // Check subscription status (bound to this session; latest request wins).
  const refreshSubscription = useCallback(async () => {
    if (!userId || !mountedRef.current || genRef.current !== gen) return;
    const requestId = ++subRequestRef.current;
    const isCurrent = () => mountedRef.current && genRef.current === gen && subRequestRef.current === requestId;
    try {
      const { data, error } = await supabase.functions.invoke("check-subscription");
      if (!isCurrent()) return;
      if (error) { console.error("check-subscription error:", error); return; }
      if (data) {
        setSubState({ gen, value: data as SubscriptionInfo });
        // Also refresh profile tier from response
        if (data.tier) {
          setProfileState(prev => prev && prev.gen === gen && prev.value ? { gen, value: { ...prev.value, tier: data.tier } } : prev);
        }
      }
    } catch (err) {
      if (!isCurrent()) return;
      console.error("refreshSubscription error:", err);
    }
  }, [userId, gen]);

  // Auto-check subscription on login and periodically
  useEffect(() => {
    if (!userId) return;
    refreshSubscription();
    const interval = setInterval(refreshSubscription, 60_000); // every minute
    return () => clearInterval(interval);
  }, [userId, refreshSubscription]);

  const hasPurchased = useCallback((skuOrProductId: string) => {
    // Retained checkers from an earlier session or after unmount never grant.
    if (!mountedRef.current || genRef.current !== gen) return false;
    if (adminMode) return true;
    return purchases.some(p =>
      (p.sku_key === skuOrProductId || p.product_id === skuOrProductId) && p.status === "completed"
    );
  }, [purchases, adminMode, gen]);

  const userTier = subscription?.tier || profile?.tier || "basic";

  return (
    <AdminContext.Provider value={{
      isAdmin, adminMode, setAdminMode, userTier, user, profile,
      subscription, purchases, hasPurchased, refreshSubscription,
    }}>
      {children}
    </AdminContext.Provider>
  );
}
